import React, { PropsWithChildren, useState } from 'react'
import { EnrollmentStatus } from '../../swagger'
import { enrollments as enrollmentsApi } from '../../api/swagger'
import { defaultPageSize } from '../Pagination/TableFooterPagination'
import EnrollmentRowInfo from '../Interfaces/EnrollmentRowInfo'

export const ENROLLMENT_MIN_SEARCH_LENGTH = 3

export enum ApplicationTypeFilterOptions {
  AllTypes = 'All Application Types',
  New = 'New',
  Returning = 'Returning',
  Invitation = 'Invitation',
}

export interface EnrollmentFilters {
  applicationType: ApplicationTypeFilterOptions
  enrollmentStatus: string
  tutorId: string | number
}

export const enrollmentStatusFilterOptions = {
  AllEnrollmentStatuses: 'All Enrollment Statuses',
  ...EnrollmentStatus,
}

export const defaultTutorFilterOption = 'All Tutors'

export const defaultProgramDetailsContextValue = {
  /** Available filters for the enrollments. */
  enrollmentFilters: {
    applicationType: ApplicationTypeFilterOptions.AllTypes,
    enrollmentStatus: enrollmentStatusFilterOptions.AllEnrollmentStatuses,
    tutorId: defaultTutorFilterOption as EnrollmentFilters['tutorId'],
  },

  /** Search value for the enrollments. */
  searchEnrollments: '',

  /** Pagination info for the enrollments. */
  enrollmentsPagination: {
    totalCount: 0,
    pageSize: defaultPageSize,
    page: 1,
  },

  /** Enrollments for a program. */
  enrollments: [] as EnrollmentRowInfo[],

  fetchEnrollments: async (programKey: number): Promise<void> => {
    console.warn(
      `The ProgramDetailsContext.fetchEnrollments was called with ${programKey}. Did you forget to use a ProgramDetailsProvider?`
    )
  },

  handleEnrollmentsPageChange: (page: number): void => {
    console.warn(
      `The ProgramDetailsContext.handleEnrollmentsPageChange was called with ${page} value. Did you forget to use a ProgramDetailsProvider?`
    )
  },

  handleEnrollmentsPageSizeChange: (pageSize: number): void => {
    console.warn(
      `The ProgramDetailsContext.handleEnrollmentsPageSizeChange was called with ${pageSize} value. Did you forget to use a ProgramDetailsProvider?`
    )
  },

  updateEnrollmentFilters: (filters: EnrollmentFilters): void => {
    console.warn(
      `The ProgramDetailsContext.updateEnrollmentFilters was called with ${filters} value. Did you forget to use a ProgramDetailsProvider?`
    )
  },

  updateSearchEnrollments: (search: string): void => {
    console.warn(
      `The ProgramDetailsContext.updateSearchEnrollments was called with ${search} value. Did you forget to use a ProgramDetailsProvider?`
    )
  },

  resetContextToDefaults: (): void => {
    console.warn(
      `The ProgramDetailsContext.resetContextToDefaults was called. Did you forget to use a ProgramDetailsProvider?`
    )
  },
}

export const ProgramDetailsContext = React.createContext(
  defaultProgramDetailsContextValue
)

export type TestProgramDetailsContextConfig =
  typeof defaultProgramDetailsContextValue

export interface TestProgramDetailsContextProps extends PropsWithChildren {
  testConfig?: Partial<TestProgramDetailsContextConfig>
}

export const useProgramDetailsContext =
  (): typeof defaultProgramDetailsContextValue =>
    React.useContext(ProgramDetailsContext)

const ProgramDetailsProvider: React.FC<TestProgramDetailsContextProps> = ({
  testConfig,
  children,
}) => {
  const [searchEnrollments, setSearchEnrollments] = useState('')
  const [searchEnrollmentInvites, setSearchEnrollmentInvites] = useState('')

  const [enrollments, setEnrollments] = useState<EnrollmentRowInfo[]>([])

  const [enrollmentsPagination, setEnrollmentsPagination] = useState({
    totalCount: 0,
    pageSize: defaultPageSize,
    page: 1,
  })

  const [enrollmentFilters, setEnrollmentFilters] = useState<EnrollmentFilters>(
    {
      applicationType: ApplicationTypeFilterOptions.AllTypes,
      enrollmentStatus: enrollmentStatusFilterOptions.AllEnrollmentStatuses,
      tutorId: defaultTutorFilterOption,
    }
  )

  const fetchEnrollments = async (programKey: number) => {
    const tutorActorKey =
      enrollmentFilters.tutorId !== defaultTutorFilterOption
        ? Number(enrollmentFilters.tutorId)
        : undefined

    const enrollmentStatus = (
      enrollmentFilters.enrollmentStatus !==
      enrollmentStatusFilterOptions.AllEnrollmentStatuses
        ? enrollmentFilters.enrollmentStatus
        : undefined
    ) as EnrollmentStatus

    const search =
      searchEnrollments.trim().length >= ENROLLMENT_MIN_SEARCH_LENGTH
        ? searchEnrollments
        : undefined

    const fetchedEnrollments = await enrollmentsApi.fetchEnrollments({
      programKey,
      tutorActorKey,
      enrollmentStatus,
      search,
      page: enrollmentsPagination.page,
      pageSize: enrollmentsPagination.pageSize,
      year: undefined,
      /**
       * Since the sortEnrollmentsByStudentName helper was removed
       * from the EnrollmentsTable component and the order of the enrollments
       * is now determined by the backend, we need to pass the sortBy property
       * to the endpoint to get the same order as before.
       */
      orderBy: ['studentLastName ASC', 'studentName ASC'],
    })

    const enrollments = fetchedEnrollments.enrollments.map((enrollment) => {
      return {
        rowId: `${enrollment.studentKey}${enrollment.programKey}`,
        ...enrollment,
      }
    })

    setEnrollments(enrollments)

    if (fetchedEnrollments.pagination) {
      const { page, pageSize, totalCount } = fetchedEnrollments.pagination
      setEnrollmentsPagination({
        page,
        pageSize,
        totalCount,
      })
    }
  }

  const handleEnrollmentsPageChange = (page: number) => {
    setEnrollmentsPagination({
      ...enrollmentsPagination,
      page: page + 1,
    })
  }

  const handleEnrollmentsPageSizeChange = (pageSize: number) => {
    setEnrollmentsPagination({ ...enrollmentsPagination, pageSize })
  }

  const resetContextToDefaults = () => {
    setEnrollmentFilters(defaultProgramDetailsContextValue.enrollmentFilters)
    setSearchEnrollments(defaultProgramDetailsContextValue.searchEnrollments)
    setEnrollmentsPagination(
      defaultProgramDetailsContextValue.enrollmentsPagination
    )
    setEnrollments(defaultProgramDetailsContextValue.enrollments)
  }

  const value = {
    enrollmentFilters,
    enrollments,
    enrollmentsPagination,
    searchEnrollmentInvites,
    searchEnrollments,

    /** functions */
    fetchEnrollments,
    handleEnrollmentsPageChange,
    handleEnrollmentsPageSizeChange,
    updateEnrollmentFilters: setEnrollmentFilters,
    updateSearchEnrollmentInvites: setSearchEnrollmentInvites,
    updateSearchEnrollments: setSearchEnrollments,
    resetContextToDefaults,
    ...testConfig,
  }

  return (
    <ProgramDetailsContext.Provider value={value}>
      {children}
    </ProgramDetailsContext.Provider>
  )
}

export default ProgramDetailsProvider
